import { EnvelopeSimple, LinkedinLogo, XLogo, MapPin } from '@phosphor-icons/react';
import { PageHero, Reveal, Chapter } from '../lib/motion';
import { CONTACT_EMAIL, LINKEDIN_URL, X_URL } from '../data/site';
import { useSEO } from '../lib/seo';

const About = () => {
  useSEO({
    title: 'About — Kumar, Founder of Vox Meridian',
    description: 'Built on practical factory-floor experience, driven by a global vision. Vox Meridian connects buyers and service providers across 10 markets.',
    path: '/about',
  });
  return (
  <div data-testid="about-page">
    <PageHero
      eyebrow="About Vox Meridian"
      lines={[<>Built on practical experience.</>, <span key="l2">Driven by a <span className="text-volt">global vision.</span></span>]}
      sub="One founder. Ten markets. A straightforward way for businesses to find capable industrial service providers."
    />

    <section className="border-b border-line bg-ink">
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
        <div className="max-w-3xl">
          <Chapter no="01" label="The founder" title="I'm Kumar." />
          <div className="mt-10 space-y-6">
            <Reveal>
              <p className="text-base leading-relaxed text-steel">
                I&rsquo;m Kumar — founder of Vox Meridian, from Malaysia.
              </p>
            </Reveal>
            <Reveal delay={0.06}>
              <p className="text-base leading-relaxed text-steel">
                My experience on the factory floor has taught me the importance of reliable service,
                skilled people, and getting work done properly. Behind every successful operation are
                people whose expertise and commitment keep things moving.
              </p>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="text-base leading-relaxed text-steel">
                I started Vox Meridian with a simple belief: capable service providers deserve greater
                visibility, and businesses need an easier way to find the right expertise.
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>

    <section className="border-b border-line bg-ink bg-grid">
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
        <div className="max-w-3xl">
          <Chapter no="02" label="Ten markets, one founder" title="Every match, by hand." />
          <div className="mt-10 space-y-6">
            <Reveal>
              <p className="text-base leading-relaxed text-steel">
                Based in Malaysia, I&rsquo;m building Vox Meridian for industrial businesses across
                Singapore, Malaysia, the UAE, the United Kingdom, Australia, the United States,
                Canada, South Africa, India and across Europe.
              </p>
            </Reveal>
            <Reveal delay={0.06}>
              <p className="text-base leading-relaxed text-steel">
                Working market by market, provider by provider, lets me learn directly from buyers
                and providers, build relationships, and keep matching quality high. Through a
                straightforward request-for-quotation process, our aim is to help businesses share
                their requirements and connect with suitable industrial service providers.
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>

    <section className="border-b border-line bg-ink">
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
        <div className="max-w-3xl">
          <Chapter no="03" label="Building one step at a time" title="Earn trust through useful work." />
          <div className="mt-10 space-y-6">
            <Reveal>
              <p className="text-base leading-relaxed text-steel">
                I&rsquo;m developing Vox Meridian alongside my full-time work. This is an early-stage
                business, and my priority is to listen, understand real needs, and earn trust through
                useful work.
              </p>
            </Reveal>
            <Reveal delay={0.06}>
              <p className="text-base leading-relaxed text-steel">
                The long-term vision is a global marketplace that helps service providers reach new
                customers and makes finding services easier across industries and borders. That vision
                begins with making the first connections valuable for both sides.
              </p>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="mt-10 border-l-2 border-volt pl-6">
                <p className="font-display text-xl font-bold leading-snug text-parchment sm:text-2xl">
                  &ldquo;I believe people who do good work deserve the opportunity to be found. That
                  belief is why I started Vox Meridian.&rdquo;
                </p>
                <p className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-steel">Kumar · Founder, Vox Meridian</p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>

    <section className="bg-ink bg-grid">
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
        <Chapter no="04" label="Contact" title="Talk to the founder directly." />
        <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Reveal>
            <a href={`mailto:${CONTACT_EMAIL}`} data-testid="about-contact-email" className="group block border border-line bg-surface p-8 transition-colors duration-300 hover:border-volt/60">
              <EnvelopeSimple size={26} className="text-volt" />
              <p className="mono-label mt-5">Email</p>
              <p className="mt-2 break-all text-sm text-parchment transition-colors duration-200 group-hover:text-volt">{CONTACT_EMAIL}</p>
            </a>
          </Reveal>
          <Reveal delay={0.07}>
            <a href={X_URL} target="_blank" rel="noopener noreferrer" data-testid="about-contact-x" className="group block border border-line bg-surface p-8 transition-colors duration-300 hover:border-volt/60">
              <XLogo size={26} className="text-volt" />
              <p className="mono-label mt-5">X</p>
              <p className="mt-2 text-sm text-parchment transition-colors duration-200 group-hover:text-volt">@vox_meridian</p>
            </a>
          </Reveal>
          <Reveal delay={0.14}>
            <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" data-testid="about-contact-linkedin" className="group block border border-line bg-surface p-8 transition-colors duration-300 hover:border-volt/60">
              <LinkedinLogo size={26} className="text-volt" />
              <p className="mono-label mt-5">LinkedIn</p>
              <p className="mt-2 text-sm text-parchment transition-colors duration-200 group-hover:text-volt">Vox Meridian</p>
            </a>
          </Reveal>
          <Reveal delay={0.21}>
            <div className="border border-line bg-surface p-8" data-testid="about-location">
              <MapPin size={26} className="text-volt" />
              <p className="mono-label mt-5">Based in</p>
              <p className="mt-2 text-sm text-parchment">Malaysia · serving 10 markets</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  </div>
  );
};

export default About;
